"use client";
import { useMemo } from "react";
import { getColorValue, ColorType } from "@/theme/colors";
const DAYS_LONG = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const CX = 100;
const CY = 100;
const OUTER_R = 88;
const TICKS = Array.from({ length: 12 }, (_, i) => {
    const rad = (i * 30 - 90) * (Math.PI / 180);
    const isCardinal = i % 3 === 0;
    const inner = isCardinal ? 75 : 82;
    const hourLabel = i === 0 ? "12" : String(i);
    return {
        hourLabel,
        x1: CX + Math.cos(rad) * inner,
        y1: CY + Math.sin(rad) * inner,
        x2: CX + Math.cos(rad) * OUTER_R,
        y2: CY + Math.sin(rad) * OUTER_R,
        isCardinal,
    };
});
function handEnd(angleDeg: number, length: number) {
    const rad = (angleDeg - 90) * (Math.PI / 180);
    return { x: CX + Math.cos(rad) * length, y: CY + Math.sin(rad) * length };
}
export interface CclockviewProps {
    date: Date;
}
export function Cclockview({ date }: CclockviewProps) {
    const { hours, minutes, seconds, dayName, hourAngle, minuteAngle, secondAngle, } = useMemo(() => {
        const h = date.getHours();
        const m = date.getMinutes();
        const s = date.getSeconds();
        return {
            hours: h,
            minutes: m,
            seconds: s,
            dayName: DAYS_LONG[date.getDay()].toUpperCase(),
            hourAngle: ((h % 12) + m / 60) * 30,
            minuteAngle: m * 6 + s / 10,
            secondAngle: s * 6,
        };
    }, [date]);
    const hourEnd = handEnd(hourAngle, 44);
    const minuteEnd = handEnd(minuteAngle, 66);
    const secondEnd = handEnd(secondAngle, 74);
    const accentColor = getColorValue(ColorType.Accent);
    const accentDim = getColorValue(ColorType.Accent, 0.75);
    return (
      <svg viewBox="0 0 200 200" width={182} height={182} style={{ display: "block", overflow: "visible" }}>
        {/* Clock face */}
        <circle cx={CX} cy={CY} r={OUTER_R} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" strokeWidth={1}/>

        {/* Tick marks */}
        {TICKS.map((tick) => (<line key={tick.hourLabel} x1={tick.x1} y1={tick.y1} x2={tick.x2} y2={tick.y2} stroke={tick.isCardinal
                ? "rgba(255,255,255,0.45)"
                : "rgba(255,255,255,0.2)"} strokeWidth={tick.isCardinal ? 2 : 1} strokeLinecap="round"/>))}

        {/* Hour hand */}
        <line x1={CX} y1={CY} x2={hourEnd.x} y2={hourEnd.y} stroke="rgba(255,255,255,0.88)" strokeWidth={4} strokeLinecap="round"/>

        {/* Minute hand */}
        <line x1={CX} y1={CY} x2={minuteEnd.x} y2={minuteEnd.y} stroke="rgba(255,255,255,0.7)" strokeWidth={2.5} strokeLinecap="round"/>

        {/* Second hand */}
        <line x1={CX} y1={CY} x2={secondEnd.x} y2={secondEnd.y} stroke={accentColor} strokeWidth={1.2} strokeLinecap="round"/>

        {/* Center dot */}
        <circle cx={CX} cy={CY} r={3} fill="rgba(255,255,255,0.9)"/>

        {/* Digital hours */}
        <text x={167} y={80} textAnchor="end" fill="rgba(255,255,255,0.8)" fontSize={22} fontWeight={300}>
          {String(hours).padStart(2, "0")}
        </text>

        {/* Digital minutes */}
        <text x={167} y={112} textAnchor="end" fill="rgba(255,255,255,0.85)" fontSize={32} fontWeight={200}>
          {String(minutes).padStart(2, "0")}
        </text>

        {/* Day of week */}
        <text x={CX} y={164} textAnchor="middle" fill={accentDim} fontSize={9} fontWeight={500} letterSpacing={1.5}>
          {dayName}
        </text>
      </svg>
    );
}
